/**
 * Gadget Guy Media 2.0 — Brand Asset Gateway
 *
 * SINGLE source of truth for owner-approved brand asset URLs.
 * Every component that renders a logo, wordmark, or mascot MUST import from here.
 *
 * DO NOT bypass this file. DO NOT hardcode `/brand-v2/...` paths in components.
 * When a new pose or logo variant is delivered, update this file only.
 *
 * DO NOT regenerate, redraw, or AI-generate any brand mark or the mascot character.
 * Only crop, resize, optimize, and compose from the owner-approved supplied artwork.
 */

const base = "/brand-v2";
// Cache-buster for updated brand assets (bump this when swapping logo files).
const v = "?v=p4b2";

// ── Logo variants (Orbital G + Custom Hybrid wordmark composites) ────────
export const LOGOS = {
  stacked: `${base}/logos/stacked.png`,
  stackedDark: `${base}/logos/stacked-dark.png`,
  stackedWithTagline: `${base}/logos/stacked-with-tagline.png`,
  stackedWithTaglineDark: `${base}/logos/stacked-with-tagline-dark.png`,
  horizontal: `${base}/logos/horizontal.png${v}`,
  horizontalWithTagline: `${base}/logos/horizontal-with-tagline.png${v}`,
  orbitalG: `${base}/logos/orbital-g.png`,
  orbitalGDark: `${base}/logos/orbital-g-dark.png`,
  stackedMono: `${base}/logos/stacked-mono.png`,
  orbitalGMono: `${base}/logos/orbital-g-mono.png`,
};

// ── Wordmark (Custom Hybrid — never typed in a font) ─────────────────────
export const WORDMARKS = {
  wordmark: `${base}/wordmark/wordmark.png`,
  wordmarkDark: `${base}/wordmark/wordmark-dark.png`,
};

// ── Mascot poses (Gadget Guy — canonical character only) ─────────────────
// Each pose has PNG source + WebP for perf, and a 2x retina variant.
// `mv` cache-buster bumps when a pose asset is swapped.
const mv = "?v=p4b3";
const mascotAsset = (name) => ({
  png: `${base}/mascot/${name}.png${mv}`,
  webp: `${base}/mascot/${name}.webp${mv}`,
  png2x: `${base}/mascot/${name}-2x.png${mv}`,
  webp2x: `${base}/mascot/${name}-2x.webp${mv}`,
});

export const MASCOT = {
  heroDefault: mascotAsset("hero-default"),
  thumbsUp: mascotAsset("thumbs-up"),
  pointing: mascotAsset("pointing"),
  presenting: mascotAsset("presenting"),
  welcoming: mascotAsset("welcoming"),
  laptop: mascotAsset("laptop"),
  tablet: mascotAsset("tablet"),
  waving: mascotAsset("waving"),
};

// ── Favicon / Open Graph (derived from approved AppIcon set) ─────────────
export const FAVICON = {
  ico: `${base}/favicon/favicon.ico`,
  size32: `${base}/favicon/favicon-32.png`,
  size48: `${base}/favicon/favicon-48.png`,
  size192: `${base}/favicon/favicon-192.png`,
  size256: `${base}/favicon/favicon-256.png`,
  size512: `${base}/favicon/favicon-512.png`,
  appleTouch: `${base}/favicon/apple-touch-icon.png`,
  ogImage: `${base}/favicon/og-image.png`,
};

// ── Reference material (kept out of production render paths) ─────────────
// If a placement produces an unfaithful-looking render, the reference sheets
// are the correction source — visually match before shipping.
export const REFERENCE = {
  logoMaster: `${base}/logos-raw/Reference/APPROVED_MASTER_REFERENCE.png`,
  mascotSheet: `${base}/mascot-raw/07_Reference/Gadget_Guy_Canonical_Approved_Asset_Sheet.png`,
};
